import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Grid,
    Paper,
    Button,
    Alert,
    IconButton,
} from '@mui/material';
import { parseISO, addSeconds, isAfter } from 'date-fns';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { InfoOutlined } from '@mui/icons-material';
import { AppContext, SttFullDialog, SttFormText } from '../../../sporttia/all';
import { getErrorMessage } from '../../../lib/utils';
import { translatePlaceholders } from '../../../lib/translatePlaceholders';
import BookingOperateDialog from './BookingOperateDialog';
import { getPath } from '../../Pages';
import BookingSectionMessages from './BookingSectionMessages';
import BookingSectionMainData from './BookingSectionMainData';
import BookingSectionOccupants from './BookingSectionOccupants';
import BookingSectionPrice from './BookingSectionPrice';
import DataphonePaymentDialog from '../../../components/dialogs/DataphonePaymentDialog';
import POSStripePaymentDialog from '../../../components/stripe/POSStripePaymentDialog';
import constants from '../../../config/constants';
import useBookingsService from '../../../services/BookingsService';
import { useLoader } from '../../../lib/hooks';
import CashdroControlDialog from '../../../components/dialogs/CashdroControlDialog';
import BookingSectionGames from './BookingSectionGames';
import { PosPaymentParams } from '../../../types/payment';

export default function Booking({
    open = false,
    idSC,
    column,
    piece,
    ini,
    end,
    onClose,
    forceFullRates = false,
}: any) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [notes, setNotes] = useState<any>();
    const [sc, setSc] = useState<any>();
    const [ImSCOwner, setImSCOwner] = useState(false);
    const [timeini, setTimeini] = useState<any>();
    const [timeend, setTimeend] = useState<any>();
    const [staticBookingMsg, setStaticBookingMsg] = useState<any>();
    const [isReadyToBook, setIsReadyToBook] = useState(false);
    const [bookingTitle, setBookingTitle] = useState<string | null>(null);
    const [facilityFull, setFacilityFull] = useState<{
        isForGame?: boolean;
        generateOccupantUnlocker?: string | null;
    } | null>(null);

    const [level, setLevel] = useState('');
    const [gender, setGender] = useState('MALE');

    // Open popups
    const [openOperate, setOpenOperate] = useState(false);

    // Fares, rates & prices
    const [rateType, setRateType] = useState(constants.rate.types.complete);
    const [finalPrice, setFinalPrice] = useState<any>();
    const [modifiedPrice, setModifiedPrice] = useState<any>();

    // POS (datafono)
    const [posPaymentParams, setPosPaymentParams] = useState<PosPaymentParams<{
        idBooking: number;
    }> | null>(null);

    const [occupants, setOccupants] = useState<any[]>([]);
    const [alfaOccupant, setAlfaOccupant] = useState<any>();

    const CASHDRO = cxt?.sc?.cashdroConnected;
    const [cashdroPayment, setCashdroPayment] = useState(false);
    const [cashdroPaymentAmount, setCashdroPaymentAmount] = useState(0);

    const bookingPrivilege = true;
    const fares = undefined;

    const isForGame = facilityFull?.isForGame;
    const [privateGame, setPrivateGame] = useState(true);
    // const [isForGame, setIsForGame] = useState(false);

    const loadSC = () => {
        cxt.api('GET', `/scs/${idSC}`, {
            success: (response: any) => {
                setSc(response.sc);
                // Am I an admin of the sports center ?
                setImSCOwner(
                    cxt.user?.role === constants.roles.sportcenter &&
                        cxt.sc?.id === response.sc.id,
                );
            },
        });
    };

    const loadFacility = () => {
        cxt.api('GET', `/facilities/${column.facility.id}`, {
            success: (response: any) => {
                setFacilityFull(response.facility);
            },
            error: (error: any) => {
                cxt.showMessage('E', getErrorMessage(error));
            },
        });
    };

    const onClickBook = () => {
        // If price is 0 we don't show the booking operating dialog
        if (finalPrice > 0) {
            setOpenOperate(true);
        } else {
            book();
        }
    };

    const bookingsService = useBookingsService();
    const createBookingMutation = bookingsService.useCreateBooking({
        onSuccess: (response: any) => {
            setOpenOperate(false);

            const { tpv, booking } = response;

            if (tpv) {
                // Pay with TPV

                if (
                    tpv?.payment?.paymentForm ===
                    constants.payment.paymentForms.dataphoneConnected.name
                ) {
                    // params for the POS dialog (datáfono físico)
                    setPosPaymentParams({
                        amount: tpv.price ?? 0,
                        concept: tpv.concept ?? '',
                        idTpv: tpv.id ?? 0,
                        returnParams: {
                            idBooking: booking?.id,
                        },
                        sc: {
                            id: tpv?.sc?.id,
                            scName: tpv?.sc?.short,
                            entityName: tpv?.sc?.customer?.name,
                            address: tpv?.sc?.address,
                            cif: tpv?.sc?.customer?.cif,
                            phone: tpv?.sc?.phonePublic,
                        },
                    });
                } else {
                    navigate(getPath('tpvSwitcher', { id: response.tpv.id }));
                }
            } else if (booking) {
                // Si es un partido pero está dentro del período de cancelación, tratarlo como reserva normal
                if (isForGame) {
                    if (cxt.user?.role === constants.roles.sportcenter) {
                        navigate(getPath('scGame', { id: booking.id }));
                    } else if (booking.signupCode) {
                        navigate(
                            `/games/${booking.id}?code=${booking.signupCode}`,
                        );
                    } else {
                        navigate(`/games/${booking.id}`);
                    }
                } else {
                    navigate(getPath('booked', { id: booking.id }));
                }
            }
        },
        onError: (error: any) => {
            const message = translatePlaceholders(getErrorMessage(error), t);
            cxt.showMessage('E', message);
            setStaticBookingMsg(message);
        },
    });

    /**
     * Proceed to book
     */
    const book = (p: { paymentForm?: string } = {}) => {
        // Patch: people is traying to book pushing many times to book
        // We avoid this not allowing to push the button every X seconds
        const lastBookingTime = localStorage.getItem('lastBookingTime');
        const secondsBetweenBooking = 15;
        if (!lastBookingTime) {
            localStorage.setItem('lastBookingTime', new Date().toISOString());
        } else {
            const lastTime = parseISO(lastBookingTime);
            const allowedTime = addSeconds(lastTime, secondsBetweenBooking);
            if (isAfter(allowedTime, new Date())) {
                cxt.showMessage('S', t('booking.notAllowedToBookToSoon'));
            } else {
                localStorage.setItem(
                    'lastBookingTime',
                    new Date().toISOString(),
                );
            }
        }

        setStaticBookingMsg(null);

        if (CASHDRO && p.paymentForm === 'CASHDRO') {
            setCashdroPayment(true);
            setCashdroPaymentAmount(modifiedPrice || finalPrice);
        } else {
            createBooking(p.paymentForm);
        }
    };

    const createBooking = (paymentForm: any) => {
        // Build occupants for API call
        let occupantsParam = occupants?.map((occupant: any) => {
            const rate = occupant?.selectedRate?.prices?.map((price: any) => ({
                id: price.id,
                duration: price.duration,
            }));

            // Entrada impersonal: el servidor resuelve el usuario invitado del
            // centro y `quantity` se expande en N entradas. No se envía idUser.
            if (occupant.guest) {
                return {
                    guest: true,
                    quantity: occupant.quantity ?? 1,
                    rate,
                };
            }

            return {
                idUser: occupant.user.id,
                idBoleto: occupant.selectedBoleto && occupant.selectedBoleto.id,
                rate,
                newUser: occupant.newUser,
            };
        });

        const individual =
            rateType === (constants.rate.types.individual as unknown);

        if (isForGame && occupantsParam) {
            occupantsParam = occupantsParam.slice(0, 1);
        }

        const isCenterGame =
            cxt.user?.role === constants.roles.sportcenter && isForGame;
        const idUserValue = isCenterGame
            ? cxt.user?.id
            : alfaOccupant?.user?.id;

        createBookingMutation.mutate({
            params: {
                idFacility: column.facility.id,
                ini: timeini,
                end: timeend,
                notes,
                price: modifiedPrice || undefined,
                individual,
                paymentForm,
                idUser: idUserValue,
                name: bookingTitle,
                occupants: occupantsParam,
                level,
                gender,
                isPublic: !privateGame,
                isWithinGameCancellationPeriod: forceFullRates,
            },
        });
    };

    /**
     * Occupants change
     */
    useEffect(() => {
        const alfaOcc = occupants && occupants.length > 0 ? occupants[0] : null;
        let allOccupantsWithRateOrBoleto = true;

        setAlfaOccupant(alfaOcc);

        // Calculate final price
        if (occupants) {
            let totalPrice = 0;
            occupants.forEach((occupant: any) => {
                if (occupant.selectedRate) {
                    // Las entradas impersonales multiplican la tarifa por la
                    // cantidad de entradas de la línea.
                    totalPrice +=
                        occupant.selectedRate.total * (occupant.quantity ?? 1);
                }

                // All occupants with rate or boleto ???
                if (!occupant.selectedRate && !occupant.selectedBoleto) {
                    allOccupantsWithRateOrBoleto = false;
                }
            });
            setFinalPrice(totalPrice);
        }

        // Is it ready to book ???
        let ready = false;
        if (
            cxt.user?.role === constants.roles.sportcenter &&
            isForGame &&
            !forceFullRates
        ) {
            ready = true;
        } else {
            ready = alfaOcc?.user && allOccupantsWithRateOrBoleto;
        }
        setIsReadyToBook(ready);

        // Set new booking title
        setBookingTitle(occupants[0]?.name ?? null);
    }, [occupants, cxt.user?.role, isForGame, forceFullRates]);
    /**
     * Init: piece or sports center id input change
     */
    useEffect(() => {
        // Load all data from sports center
        if (idSC) {
            loadSC();
        }

        // Set time ini & end
        if (piece) {
            setTimeini(ini);
            setTimeend(end);
        }

        // Load facility full data
        if (column && column.facility) {
            loadFacility();
        }
    }, [piece, idSC]);

    const [isMutationsLoading, mutationsLoader] = useLoader([
        createBookingMutation.status,
    ]);

    const handleChangeLevel = (input: any) => {
        // Permitir solo números con hasta dos decimales
        const regex = /^(\d{0,1}(\.\d{0,2})?)?$/;

        if (regex.test(input) && input <= 6) {
            setLevel(input);
        }
    };

    return (
        <SttFullDialog
            open={open}
            title={t('labels.booking')}
            onClose={onClose}
        >
            {mutationsLoader}

            {sc && piece && (
                <Container>
                    <Box mb={5}>
                        <Grid container spacing={3} justifyContent="center">
                            <Grid size={{ md: 8 }}>
                                <BookingSectionMessages
                                    bookingPrivilege={bookingPrivilege}
                                    sc={sc}
                                />

                                <BookingSectionMainData
                                    ImSCOwner={ImSCOwner}
                                    sc={sc}
                                    piece={piece}
                                    column={column}
                                    timeini={timeini}
                                    timeend={timeend}
                                    onChangeTimeini={setTimeini}
                                    onChangeTimeend={setTimeend}
                                    notes={notes}
                                    onChangeNotes={setNotes}
                                />

                                {isForGame && (
                                    <BookingSectionGames
                                        level={level}
                                        gender={gender}
                                        privateGame={privateGame}
                                        onChangeLevel={handleChangeLevel}
                                        onChangeGender={setGender}
                                        onChangePrivateGame={setPrivateGame}
                                    />
                                )}

                                {/* Booking headline box. */}
                                <Box mt={3}>
                                    <Paper>
                                        <Box p={3}>
                                            <Grid
                                                container
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <SttFormText
                                                    grid
                                                    md={11}
                                                    sm={11}
                                                    xs={11}
                                                    disabled={
                                                        cxt.logged &&
                                                        cxt.user?.role !==
                                                            constants.roles
                                                                .sportcenter
                                                    }
                                                    caption={t(
                                                        'booking.headline',
                                                    )}
                                                    defVal={bookingTitle}
                                                    onChange={({
                                                        value,
                                                    }: any) =>
                                                        setBookingTitle(value)
                                                    }
                                                />
                                                <Grid
                                                    size={{
                                                        md: 1,
                                                        sm: 1,
                                                        xs: 1,
                                                    }}
                                                >
                                                    <IconButton
                                                        aria-label="delete"
                                                        disabled={
                                                            cxt.logged &&
                                                            cxt.user?.role !==
                                                                constants.roles
                                                                    .sportcenter
                                                        }
                                                        onClick={() =>
                                                            setBookingTitle(
                                                                (occupants[0] &&
                                                                    occupants[0]
                                                                        .name) ||
                                                                    null,
                                                            )
                                                        }
                                                    >
                                                        <SettingsBackupRestoreIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Paper>
                                </Box>

                                {/* Aviso de tarifa completa forzada por periodo de cancelación */}
                                {isForGame && forceFullRates && (
                                    <Box
                                        style={{
                                            marginBottom: 24,
                                            marginTop: 16,
                                            padding: '12px 16px',
                                            backgroundColor: '#e3f2fd',
                                            borderRadius: 4,
                                            border: '1px solid #90caf9',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                        }}
                                    >
                                        <InfoOutlined
                                            style={{
                                                color: '#1976d2',
                                                fontSize: 20,
                                                marginRight: 8,
                                            }}
                                        />
                                        <Box
                                            style={{
                                                color: '#1565c0',
                                                fontSize: 14,
                                            }}
                                        >
                                            {t('game.payingWithFullRate')}
                                        </Box>
                                    </Box>
                                )}
                                <BookingSectionOccupants
                                    timeini={timeini}
                                    timeend={timeend}
                                    piece={piece}
                                    column={column}
                                    sc={sc}
                                    rateType={rateType}
                                    setRateType={setRateType}
                                    ImSCOwner={ImSCOwner}
                                    occupants={occupants}
                                    setOccupants={setOccupants}
                                    isForGame={isForGame}
                                    forceFullRates={forceFullRates}
                                    generateOccupantUnlocker={
                                        facilityFull?.generateOccupantUnlocker
                                    }
                                />

                                {isReadyToBook ? (
                                    <>
                                        {/* --- Final price --- */}
                                        {finalPrice !== null && (
                                            <Box mt={2}>
                                                <BookingSectionPrice
                                                    price={finalPrice}
                                                    sc={sc}
                                                    modify={ImSCOwner}
                                                    onChangePrice={(
                                                        price: any,
                                                    ) => {
                                                        // Accept comma as a
                                                        // decimal separator so
                                                        // values like "0,50"
                                                        // are parsed correctly.
                                                        const parsed = Number(
                                                            String(
                                                                price,
                                                            ).replace(',', '.'),
                                                        );
                                                        if (
                                                            price === '' ||
                                                            Number.isNaN(
                                                                parsed,
                                                            ) ||
                                                            parsed <= 0
                                                        ) {
                                                            cxt.showMessage(
                                                                'E',
                                                                t(
                                                                    'booking.priceMustBePositive',
                                                                ),
                                                            );
                                                            return false;
                                                        }
                                                        setFinalPrice(parsed);
                                                        setModifiedPrice(
                                                            parsed,
                                                        );
                                                        return true;
                                                    }}
                                                />
                                            </Box>
                                        )}

                                        {/* --- Button to book --- */}
                                        <Box mt={4} mb={2}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="primary"
                                                startIcon={<DoneOutlineIcon />}
                                                onClick={onClickBook}
                                            >
                                                {t('timetable.book')}
                                            </Button>
                                        </Box>
                                    </>
                                ) : (
                                    <Box mt={3}>
                                        <Alert severity="error">
                                            {t('booking.isNotReadyToBook')}
                                        </Alert>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Box>

                    {!isMutationsLoading && (
                        <BookingOperateDialog
                            open={openOperate}
                            staticMsg={staticBookingMsg}
                            mship={alfaOccupant && alfaOccupant.mship}
                            rateType={rateType}
                            fares={fares}
                            onClose={() => setOpenOperate(false)}
                            onBook={book}
                            sc={sc}
                            finalPrice={finalPrice}
                        />
                    )}
                </Container>
            )}

            {cxt.sc?.sporttiaStripeLocationId && posPaymentParams !== null ? (
                <POSStripePaymentDialog
                    paymentData={posPaymentParams}
                    onClose={() => setPosPaymentParams(null)}
                    onSuccess={() => {
                        if (posPaymentParams.returnParams.idBooking) {
                            navigate(
                                getPath('booked', {
                                    id: posPaymentParams.returnParams.idBooking,
                                }),
                            );
                        }
                    }}
                />
            ) : (
                <DataphonePaymentDialog
                    open={posPaymentParams !== null}
                    paymentData={posPaymentParams}
                    onClose={() => setPosPaymentParams(null)}
                    onSuccess={(_ticket: any, returnParams: any) => {
                        if (returnParams.idBooking) {
                            navigate(
                                getPath('booked', {
                                    id: returnParams.idBooking,
                                }),
                            );
                        }
                    }}
                />
            )}

            {cashdroPayment && (
                <CashdroControlDialog
                    open={cashdroPayment}
                    amount={cashdroPaymentAmount}
                    onClose={() => {
                        setCashdroPayment(false);
                        setCashdroPaymentAmount(0);
                    }}
                    onSuccess={() => {
                        // Aquí a la fuerza pq POST /bookings ahora por la cara tarda 10+ segundos
                        setCashdroPayment(false);
                        setCashdroPaymentAmount(0);
                        createBooking('CASHDRO');
                    }}
                />
            )}
        </SttFullDialog>
    );
}
